import type { GridDensity, GridFilterItem, GridLogicOperator } from '@mui/x-data-grid-premium';
import { useCallback, useEffect, useState } from 'react';

import type { GridView, GridViewState, StoredGridViews } from './gridViewState';
import { EMPTY_STORED_VIEWS } from './gridViewState';

const STORAGE_PREFIX = 'liftai.gridViews.';

// `storage` only fires in other tabs; this carries the same news within this one.
const SAME_TAB_EVENT = 'liftai.gridViews.written';

/** A payload with no version is read as 1; one from a newer build is left alone. */
const STORAGE_VERSION = 1;

const storageKey = (storeKey: string) => `${STORAGE_PREFIX}${storeKey}`;

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const stringArray = (value: unknown): string[] | undefined =>
  Array.isArray(value) && value.every((entry) => typeof entry === 'string')
    ? (value as string[])
    : undefined;

const booleanRecord = (value: unknown): Record<string, boolean> | undefined =>
  isObject(value) && Object.values(value).every((entry) => typeof entry === 'boolean')
    ? (value as Record<string, boolean>)
    : undefined;

const objectArray = (value: unknown): Record<string, unknown>[] | undefined =>
  Array.isArray(value) && value.every(isObject) ? (value as Record<string, unknown>[]) : undefined;

const DENSITIES: GridDensity[] = ['compact', 'standard', 'comfortable'];

/**
 * Keeps only the dimensions that are the right shape. All of this is replayed into
 * `restoreState`, which calls `.reduce` on a sort model and `.filter` on a grouping model
 * without checking, so one malformed key would throw on every load — and the error page's
 * own retry button lands straight back on it.
 */
const normalizeState = (value: unknown): GridViewState => {
  if (!isObject(value)) return {};
  const state: GridViewState = {};

  const columns = isObject(value.columns) ? value.columns : undefined;
  const orderedFields = stringArray(columns?.orderedFields);
  const columnVisibilityModel = booleanRecord(columns?.columnVisibilityModel);
  if (orderedFields || columnVisibilityModel) {
    state.columns = {
      ...(orderedFields ? { orderedFields } : {}),
      ...(columnVisibilityModel ? { columnVisibilityModel } : {}),
    };
  }

  const filterModel =
    isObject(value.filter) && isObject(value.filter.filterModel)
      ? value.filter.filterModel
      : undefined;
  const items = objectArray(filterModel?.items)?.filter(
    (item) => typeof item.field === 'string' && typeof item.operator === 'string',
  );
  if (items) {
    state.filter = {
      filterModel: {
        items: items as unknown as GridFilterItem[],
        ...(typeof filterModel?.logicOperator === 'string'
          ? { logicOperator: filterModel.logicOperator as GridLogicOperator }
          : {}),
      },
    };
  }

  const sortModel = objectArray(isObject(value.sorting) ? value.sorting.sortModel : undefined);
  if (sortModel) {
    state.sorting = {
      sortModel: sortModel as unknown as NonNullable<GridViewState['sorting']>['sortModel'],
    };
  }

  if (typeof value.density === 'string' && DENSITIES.includes(value.density as GridDensity)) {
    state.density = value.density as GridDensity;
  }

  const rowGroupingModel = stringArray(
    isObject(value.rowGrouping) ? value.rowGrouping.model : undefined,
  );
  if (rowGroupingModel) state.rowGrouping = { model: rowGroupingModel };

  const aggregationModel = isObject(value.aggregation) ? value.aggregation.model : undefined;
  if (isObject(aggregationModel)) {
    state.aggregation = {
      model: aggregationModel as NonNullable<GridViewState['aggregation']>['model'],
    };
  }

  const pinned = isObject(value.pinnedColumns) ? value.pinnedColumns : undefined;
  if (pinned) {
    const left = stringArray(pinned.left);
    const right = stringArray(pinned.right);
    state.pinnedColumns = { ...(left ? { left } : {}), ...(right ? { right } : {}) };
  }

  const pageSize =
    isObject(value.pagination) && isObject(value.pagination.paginationModel)
      ? value.pagination.paginationModel.pageSize
      : undefined;
  if (typeof pageSize === 'number' && Number.isFinite(pageSize) && pageSize > 0) {
    state.pagination = { paginationModel: { pageSize } };
  }

  return state;
};

const normalizeView = (value: unknown): GridView | null => {
  if (!isObject(value)) return null;
  if (typeof value.id !== 'string' || !value.id) return null;
  if (typeof value.name !== 'string' || !value.name.trim()) return null;

  return {
    id: value.id,
    name: value.name,
    state: normalizeState(value.state),
    ...(typeof value.createdAt === 'number' ? { createdAt: value.createdAt } : {}),
  };
};

/** Whatever is in storage, turned into something the rest of the feature can rely on. */
const normalizeStoredViews = (value: unknown): StoredGridViews => {
  if (!isObject(value)) return EMPTY_STORED_VIEWS;
  if (typeof value.version === 'number' && value.version > STORAGE_VERSION) {
    return EMPTY_STORED_VIEWS;
  }

  const seen = new Set<string>();
  const views = (Array.isArray(value.views) ? value.views : [])
    .map(normalizeView)
    .filter((view): view is GridView => {
      if (!view || seen.has(view.id)) return false;
      seen.add(view.id);
      return true;
    });

  const defaultViewId =
    typeof value.defaultViewId === 'string' && seen.has(value.defaultViewId)
      ? value.defaultViewId
      : null;

  return { views, defaultViewId };
};

/** A payload this build cannot understand is left alone rather than overwritten. */
const isFromNewerBuild = (raw: string): boolean => {
  try {
    const value: unknown = JSON.parse(raw);
    return isObject(value) && typeof value.version === 'number' && value.version > STORAGE_VERSION;
  } catch {
    return false;
  }
};

const readStored = (key: string): StoredGridViews => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? normalizeStoredViews(JSON.parse(raw)) : EMPTY_STORED_VIEWS;
  } catch {
    // Unreadable is the same answer as nothing saved — better than a page that will not load.
    return EMPTY_STORED_VIEWS;
  }
};

/**
 * The property page's Tickets and Spending tabs used to share their saved views with the
 * global lists. Each pair is `[shared, property tab]` storage keys.
 */
const PROPERTY_TAB_KEYS: readonly (readonly [shared: string, propertyTab: string])[] = [
  ['tickets-table-columns-visibility', 'tickets-table-property'],
  ['invoices-table-columns-visibility', 'invoices-table-property'],
];

/** The columns a property tab hides: the page already says which property it is. */
const PROPERTY_TAB_HIDDEN_FIELDS = ['property', 'portfolio'];

const PROPERTY_TAB_MIGRATION_MARKER = 'liftai.gridViewsMigrated.propertyTabKeys';

/** A default that hides both columns was, in all likelihood, set on a property tab. */
const hidesPropertyTabColumns = (view: unknown): boolean => {
  if (!isObject(view) || !isObject(view.state) || !isObject(view.state.columns)) return false;
  const visibility = view.state.columns.columnVisibilityModel;
  return (
    isObject(visibility) && PROPERTY_TAB_HIDDEN_FIELDS.every((field) => visibility[field] === false)
  );
};

/** A payload this build can safely rewrite, or `null` for one it should leave alone. */
const parseOwnPayload = (raw: string): (Record<string, unknown> & { views: unknown[] }) | null => {
  try {
    const payload: unknown = JSON.parse(raw);
    if (!isObject(payload) || !Array.isArray(payload.views)) return null;
    if (typeof payload.version === 'number' && payload.version > STORAGE_VERSION) return null;
    return { ...payload, views: payload.views };
  } catch {
    return null;
  }
};

/**
 * One-time move for the property tabs' own keys. One rule decides where a view belongs: a view
 * that hides both the Property and Portfolio columns was made on a property tab. Those views
 * are copied to the tab's own key, their default with them, and the global list stops
 * opening on such a default, though the view stays saved on it. Views that show either
 * column stay on the global list only. Nothing is deleted.
 *
 * The marker is written only once every pair is done, so a write that fails is retried on the
 * next load. Both steps are safe to repeat: the copy only fills a key that holds nothing, and
 * the default is only cleared where it still hides both columns. After that the marker keeps
 * this from running again, so a view later deleted on a property tab is never copied back.
 */
export function migratePropertyTabViews(storage: Storage): void {
  try {
    if (storage.getItem(PROPERTY_TAB_MIGRATION_MARKER) !== null) return;

    for (const [shared, propertyTab] of PROPERTY_TAB_KEYS) {
      const raw = storage.getItem(storageKey(shared));
      // Unreadable, or written by a newer build that knows its own layout: left as it is.
      const payload = raw ? parseOwnPayload(raw) : null;
      if (!payload) continue;

      const { defaultViewId } = payload;
      const defaultView =
        typeof defaultViewId === 'string'
          ? payload.views.find((view) => isObject(view) && view.id === defaultViewId)
          : undefined;
      const defaultIsPropertyTabs = hidesPropertyTabColumns(defaultView);

      // In their stored order; anything else in the payload, `version` included, as it was.
      const propertyTabViews = payload.views.filter(hidesPropertyTabColumns);
      if (propertyTabViews.length > 0 && storage.getItem(storageKey(propertyTab)) === null) {
        storage.setItem(
          storageKey(propertyTab),
          JSON.stringify({
            ...payload,
            views: propertyTabViews,
            defaultViewId: defaultIsPropertyTabs ? defaultViewId : null,
          }),
        );
      }

      if (defaultIsPropertyTabs) {
        storage.setItem(storageKey(shared), JSON.stringify({ ...payload, defaultViewId: null }));
      }
    }

    storage.setItem(PROPERTY_TAB_MIGRATION_MARKER, '1');
  } catch {
    // Storage that cannot be read or written is the same answer as nothing saved; the views
    // stay where they were, and the next load tries again.
  }
}

let propertyTabViewsMigrated = false;

/** Before the first read of the session, so no table opens on the pre-move keys. */
const migrateOnce = () => {
  if (propertyTabViewsMigrated) return;
  propertyTabViewsMigrated = true;
  try {
    migratePropertyTabViews(window.localStorage);
  } catch {
    // Where site data is blocked, reaching for `window.localStorage` at all throws.
  }
};

/**
 * Why a write did not land. The two failures need different words: one is the reader's
 * browser refusing storage, the other is this tab running older code than whatever last
 * wrote the key, where reloading is the fix.
 */
export type WriteOutcome = 'saved' | 'unavailable' | 'newer-build';

export type WriteStoredGridViews = (
  update: (previous: StoredGridViews) => StoredGridViews,
) => WriteOutcome;

/**
 * Unlike a plain `useLocalStorage`, the write reports how it went — a full or blocked store
 * otherwise swallows the error and the reader is told their view was saved.
 */
export function useStoredGridViews(storeKey: string): [StoredGridViews, WriteStoredGridViews] {
  const key = storageKey(storeKey);
  const [state, setState] = useState(() => {
    migrateOnce();
    return { key, views: readStored(key) };
  });

  // A key that changes mid-mount is a different table's views. Adjusted during render,
  // which React prefers to an effect for state that follows a prop.
  if (state.key !== key) setState({ key, views: readStored(key) });
  const stored = state.key === key ? state.views : readStored(key);

  const setStored = useCallback((next: StoredGridViews) => setState({ key, views: next }), [key]);

  useEffect(() => {
    const sync = (event: Event) => {
      const changed =
        event instanceof StorageEvent ? event.key : (event as CustomEvent<string>).detail;
      if (changed && changed !== key) return;
      setStored(readStored(key));
    };

    window.addEventListener('storage', sync);
    window.addEventListener(SAME_TAB_EVENT, sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(SAME_TAB_EVENT, sync);
    };
  }, [key, setStored]);

  const write = useCallback<WriteStoredGridViews>(
    (update) => {
      // The whole body, not just the write: where site data is blocked, reaching for
      // `window.localStorage` at all throws, and that is exactly the case this reports on.
      try {
        // Against a fresh read: another tab may have written since this one last looked.
        const raw = window.localStorage.getItem(key);
        if (raw && isFromNewerBuild(raw)) return 'newer-build';

        const next = update(readStored(key));
        window.localStorage.setItem(key, JSON.stringify({ version: STORAGE_VERSION, ...next }));
        setStored(next);
        window.dispatchEvent(new CustomEvent(SAME_TAB_EVENT, { detail: key }));
        return 'saved';
      } catch {
        return 'unavailable';
      }
    },
    [key, setStored],
  );

  return [stored, write];
}
